/**
 * RiskApplication Service — Unified API (Phase 7)
 * Replaces per-scope methods with a single parameterised interface.
 */

import apiClient from './apiClient';
import { isApiError } from '../types/common';
import type {
  RiskApplicationScope,
  RiskApplication,
  RiskApplicationCreatePayload,
  RiskApplicationUpdatePayload,
  RiskApplicationBulkPayload,
  RiskApplicationBulkResponse,
  EmployeeAppliedRisk,
} from '../types/riskApplication';

// Re-export types so callers can import from the service as before
export * from '../types/riskApplication';

// ============================================================================
// ERROR HELPERS
// ============================================================================

export class DuplicateApplicationError extends Error {
  constructor(public readonly riskId?: number) {
    super('Risco já aplicado a este destino');
    this.name = 'DuplicateApplicationError';
  }
}

function extractData<T>(response: unknown): T {
  if (
    typeof response === 'object' &&
    response !== null &&
    'data' in response
  ) {
    const r = response as { data: T };
    if (r.data !== undefined) return r.data;
  }
  return response as T;
}

function handleError(err: unknown): never {
  if (isApiError(err) && err.code === 'DUPLICATE_APPLICATION') {
    throw new DuplicateApplicationError();
  }
  throw err;
}

// ============================================================================
// SERVICE CLASS
// ============================================================================

class RiskApplicationService {
  private readonly base = '/risk-applications';

  /**
   * List applications for a scope+scopeId combination.
   */
  async list(scope: RiskApplicationScope, scopeId: number): Promise<RiskApplication[]> {
    try {
      const response = await apiClient.get<RiskApplication[]>(
        `${this.base}?scope=${scope}&scopeId=${scopeId}`
      );
      return extractData<RiskApplication[]>(response);
    } catch (err) {
      return handleError(err);
    }
  }

  /**
   * Get a single application by id.
   */
  async getById(id: number): Promise<RiskApplication | null> {
    try {
      const response = await apiClient.get<RiskApplication>(`${this.base}/${id}`);
      return extractData<RiskApplication>(response);
    } catch (err) {
      if (isApiError(err) && err.status === 404) return null;
      return handleError(err);
    }
  }

  /**
   * Apply a single risk.
   */
  async apply(payload: RiskApplicationCreatePayload): Promise<RiskApplication> {
    try {
      const response = await apiClient.post<RiskApplication>(this.base, payload);
      return extractData<RiskApplication>(response);
    } catch (err) {
      return handleError(err);
    }
  }

  /**
   * Update an existing application.
   */
  async update(id: number, payload: RiskApplicationUpdatePayload): Promise<RiskApplication> {
    try {
      const response = await apiClient.put<RiskApplication>(`${this.base}/${id}`, payload);
      return extractData<RiskApplication>(response);
    } catch (err) {
      return handleError(err);
    }
  }

  /**
   * Remove an application.
   */
  async remove(id: number): Promise<void> {
    try {
      await apiClient.delete(`${this.base}/${id}`);
    } catch (err) {
      return handleError(err);
    }
  }

  /**
   * Bulk-apply a list of risks to a scope+scopeId.
   * Returns created applications and list of riskIds that were duplicates.
   */
  async bulkApply(payload: RiskApplicationBulkPayload): Promise<RiskApplicationBulkResponse> {
    try {
      const response = await apiClient.post<RiskApplicationBulkResponse>(
        `${this.base}/bulk`,
        payload
      );
      return extractData<RiskApplicationBulkResponse>(response);
    } catch (err) {
      return handleError(err);
    }
  }

  /**
   * Get all risks that apply to an employee, aggregated across all
   * organisational scopes (company, department, occupation, employee).
   * The backend deduplicates by riskId, keeping the most specific scope.
   */
  async getEmployeeRisks(employeeId: number): Promise<EmployeeAppliedRisk[]> {
    try {
      const response = await apiClient.get<EmployeeAppliedRisk[]>(
        `${this.base}/employee/${employeeId}`
      );
      return extractData<EmployeeAppliedRisk[]>(response);
    } catch (err) {
      return handleError(err);
    }
  }
}

export const riskApplicationService = new RiskApplicationService();
export default riskApplicationService;
